import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  ogImageAlt?: string;
  // Pinterest Rich Pins - Product metadata
  productPrice?: number;
  productCurrency?: string;
  productAvailability?: "in stock" | "out of stock" | "preorder";
  productBrand?: string;
}

const DEFAULT_OG_IMAGE = "https://funzyfeets.shop/og-image.jpg";

const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "product",
  ogImageAlt,
  productPrice,
  productCurrency = "EUR",
  productAvailability = "in stock",
  productBrand = "FunzyFeets",
}: SEOHeadProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to update or create meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta) {
        meta.setAttribute("content", content);
      } else {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        meta.setAttribute("content", content);
        document.head.appendChild(meta);
      }
    };

    // Update meta description
    updateMeta("description", description);

    // Update keywords
    if (keywords) {
      updateMeta("keywords", keywords);
    }

    // Update Open Graph tags
    updateMeta("og:title", title, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", ogType, true);
    updateMeta("og:site_name", "Funzy Feets", true);
    
    // OG Image with dimensions
    updateMeta("og:image", ogImage, true);
    updateMeta("og:image:width", "1200", true);
    updateMeta("og:image:height", "630", true);
    if (ogImageAlt) {
      updateMeta("og:image:alt", ogImageAlt, true);
    }

    if (canonicalUrl) {
      updateMeta("og:url", canonicalUrl, true);
      
      // Update or create canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute("href", canonicalUrl);
      } else {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        canonical.setAttribute("href", canonicalUrl);
        document.head.appendChild(canonical);
      }
    }

    // Twitter Card - summary_large_image for better visibility
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", ogImage);
    if (ogImageAlt) {
      updateMeta("twitter:image:alt", ogImageAlt);
    }

    // Pinterest Rich Pins metadata (using Open Graph product namespace)
    if (ogType === "product" && productPrice) {
      updateMeta("og:price:amount", productPrice.toFixed(2), true);
      updateMeta("og:price:currency", productCurrency, true);
      updateMeta("product:price:amount", productPrice.toFixed(2), true);
      updateMeta("product:price:currency", productCurrency, true);
      updateMeta("product:availability", productAvailability, true);
      updateMeta("product:brand", productBrand, true);
      updateMeta("product:condition", "new", true);
      
      // Pinterest-specific tags
      updateMeta("pin:media", ogImage);
      updateMeta("pin:description", description);
    }

    // Cleanup function to remove added elements on unmount
    return () => {
      // Keep the meta tags but they'll be updated on next page
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, ogImageAlt, productPrice, productCurrency, productAvailability, productBrand]);

  return null;
};

export default SEOHead;
